import styled from '@pinecast/styles';
import * as React from 'react';

export interface ShortcutKey {
  letter: string;
  metaKey?: boolean;
  optionKey?: boolean;
}

const Wrapper = styled('span', {display: 'inline-block', fontSize: '0.6em'});
const Spacer = styled('span', {margin: '0 0.25em'});

const userAgent =
  typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : 'node';

const isMobile = userAgent.includes('mobile') || userAgent.includes('tablet');

/**
 * Whether the platform's command key is ⌘ (macOS) rather than Ctrl. The hint
 * that `KeyboardShortcut` draws and the check that `matchesShortcut` makes
 * both ask this, so a shortcut always works the way its hint says.
 */
export const isMacOS = () =>
  typeof navigator !== 'undefined' &&
  navigator.userAgent.toLowerCase().includes('macintosh');

function renderMeta() {
  if (isMacOS()) {
    return '⌘';
  }
  return 'ctrl';
}
function renderOption() {
  if (isMacOS()) {
    return '⌥';
  }
  return 'alt';
}

/**
 * Whether a `keydown` event is the shortcut that a `KeyboardShortcut` with the
 * same props shows: ⌘ and ⌥ on macOS, Ctrl and Alt everywhere else.
 *
 * The letter comes from `key`, so that it follows the keyboard layout (S on
 * Dvorak is not where it is on QWERTY). When `key` is not a Latin letter (a
 * Cyrillic layout, or the character that ⌥ types on macOS), it comes from
 * `code`, the physical key, instead.
 */
export const matchesShortcut = (
  e: Pick<KeyboardEvent, 'altKey' | 'code' | 'ctrlKey' | 'key' | 'metaKey'>,
  shortcut: ShortcutKey,
) => {
  const commandKey = isMacOS() ? e.metaKey : e.ctrlKey;
  if (commandKey !== Boolean(shortcut.metaKey)) {
    return false;
  }
  if (e.altKey !== Boolean(shortcut.optionKey)) {
    return false;
  }
  const letter = shortcut.letter.toLowerCase();
  const key = e.key.toLowerCase();
  if (/^[a-z0-9]$/.test(key)) {
    return key === letter;
  }
  return e.code === `Key${letter.toUpperCase()}` || e.code === `Digit${letter}`;
};

type AvailableStyles = Pick<
  React.CSSProperties,
  | 'alignSelf'
  | 'color'
  | 'fontSize'
  | 'fontWeight'
  | 'justifySelf'
  | 'lineHeight'
  | 'letterSpacing'
  | 'margin'
  | 'marginBottom'
  | 'marginLeft'
  | 'marginRight'
  | 'marginTop'
  | 'opacity'
  | 'textDecoration'
  | 'textShadow'
  | 'textTransform'
>;

const KeyboardShortcut = ({
  style,
  ...shortcut
}: ShortcutKey & {style?: AvailableStyles}) => {
  if (isMobile) {
    return <span />;
  }
  const modifiers = [
    shortcut.metaKey ? renderMeta() : null,
    shortcut.optionKey ? renderOption() : null,
  ].filter(x => x);

  return (
    <Wrapper style={style}>
      {modifiers.reduce(
        (acc, cur) => (
          <>
            {cur}
            <Spacer>+</Spacer>
            {acc}
          </>
        ),
        <>{shortcut.letter.toUpperCase()}</>,
      )}
    </Wrapper>
  );
};

export default KeyboardShortcut;
